import { useOAuth2 } from "@tasoskakour/react-use-oauth2";
import { authPath, routerMode, routerBase } from "../../config.json";
import { useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { enableMockServer, getEnvSettings, mockAuthUrl } from "@/lib/utils";

export interface User {
  name: string;
  nameid: string;
  unique_name: string;
  language?: string;
  session?: string;
}

export interface UseAuthResult {
  isLoggedIn: boolean;
  user: User | undefined;
  token?: string;
  loading: boolean;
  error?: string;
  login: () => void;
  logout: () => void;
  authUrl: string;
}

export function useAuth(): UseAuthResult {
  const { clientId } = getEnvSettings();
  const { data, loading, error, getAuth, logout } = useOAuth2({
    authorizeUrl: getAuthUrl(),
    clientId: clientId,
    redirectUri:
      document.location.origin +
      routerBase +
      (routerMode === "hash" ? "/" : "/callback"),
    scope: "global",
    responseType: "token",
    onSuccess: (payload) => console.log("Success", payload),
    onError: (error_) => console.log("Error", error_),
  });

  const user: User | undefined = useMemo(() => {
    if (!data?.access_token) return undefined;
    try {
      const decoded = jwtDecode(data.access_token);
      return {
        nameid: "nameid" in decoded ? (decoded.nameid as string) : "",
        unique_name:
          "unique_name" in decoded ? (decoded.unique_name as string) : "",
        name:
          "urn:fullname" in decoded ? (decoded["urn:fullname"] as string) : "",
        language:
          "urn:language" in decoded
            ? (decoded["urn:language"] as string | undefined)
            : undefined,
        session:
          "urn:session" in decoded
            ? (decoded["urn:session"] as string | undefined)
            : undefined,
      };
    } catch {
      return undefined;
    }
  }, [data?.access_token]);

  return {
    isLoggedIn: !!data?.access_token,
    token: data?.access_token,
    user,
    loading,
    error: error || undefined,
    login: getAuth,
    logout,
    authUrl: getAuthUrl(),
  };
}

function getAuthUrl(): string {
  if (!enableMockServer()) return getEnvSettings().baseUrl + authPath;
  return routerMode === "hash" ? `#${mockAuthUrl}` : mockAuthUrl;
}
